// WhatsApp phone number formatting utilities

/**
 * Cleans a phone number for storage in database (removes +, spaces, etc.)
 * @param value - Formatted phone number input
 * @returns Clean phone number string for database storage
 */
export const cleanWhatsAppNumber = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  
  // If it's already a complete international number, return as-is
  if (numbers.length >= 10) {
    return numbers;
  }
  
  // For Portuguese numbers (9 digits), add 351 prefix
  if (numbers.length === 9) {
    return '351' + numbers;
  }
  
  return numbers; // Return as-is for other cases
};

/**
 * Validates a WhatsApp number (should be at least 10 digits for international numbers)
 * @param value - Phone number to validate
 * @returns Boolean indicating if the number is valid
 */
export const validateWhatsAppNumber = (value: string): boolean => {
  const cleaned = cleanWhatsAppNumber(value);
  // Accept numbers with at least 10 digits (international format)
  // Portuguese numbers will be 12 digits (351 + 9 digits)
  // Other international numbers will be 10+ digits
  return cleaned.length >= 10;
};

/**
 * Formats a phone number from database for display
 * @param value - Clean phone number from database
 * @returns Formatted phone number for display
 */
export const displayWhatsAppNumber = (value: string): string => {
  if (!value) return '';
  
  const numbers = value.replace(/\D/g, '');
  
  // Format Portuguese numbers as +351 9XX XXX XXX
  if (numbers.length === 12 && numbers.startsWith('351')) {
    return `+${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6, 9)} ${numbers.slice(9)}`;
  } 
  // Format 9-digit numbers as Portuguese (+351)
  else if (numbers.length === 9) {
    return `+351 ${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
  }
  // Format international numbers
  else if (numbers.length >= 10) {
    // For common international formats, add some spacing
    if (numbers.length === 10) {
      // US/Canada format: +1 555 123 4567
      return `+${numbers.slice(0, 1)} ${numbers.slice(1, 4)} ${numbers.slice(4, 7)} ${numbers.slice(7)}`;
    } else if (numbers.length === 11) {
      // UK format: +44 20 1234 5678
      return `+${numbers.slice(0, 2)} ${numbers.slice(2, 4)} ${numbers.slice(4, 8)} ${numbers.slice(8)}`;
    } else {
      // Generic international format
      return `+${numbers}`;
    }
  }
  
  return `+${numbers}`;
}; 